'use client';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'primereact/tooltip';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Skeleton } from 'primereact/skeleton';
import { useShipping, formatDaysLabel, DELIVERY_TIMELINE_NOTE } from './useShipping';
import { PinCodeInput } from './PinCodeInput';
import { SavedAddressList } from './SavedAddressList';
import { getAuthUser } from '@/app/cart/_helpers';
import { DeliveryFeedbackTrigger } from './DeliveryFeedback';

/* ── Amazon Shipping "fastest delivery" card — shared across all delivery-check UIs ── */
export const FastestDeliveryCard = ({ fastestDelivery }) => {
    if (!fastestDelivery?.available) return null;
    return (
        <div style={{
            borderRadius: '0.75rem',
            border: '1.5px solid #fcd34d',
            background: '#fffbeb',
            padding: '0.85rem 1rem',
            display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
        }}>
            <div style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: '#f59e0b',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <i className="pi pi-bolt" style={{ color: '#fff', fontSize: '0.8rem' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#92400e' }}>
                    Fastest Delivery Available ⚡
                </div>
                <div style={{ fontSize: '0.78rem', color: '#92400e', marginTop: '0.2rem' }}>
                    Delivered in <strong>{fastestDelivery.timeline}</strong> at your location
                </div>
                <div style={{ fontSize: '0.68rem', color: '#b45309', fontWeight: 600, marginTop: '0.3rem' }}>
                    Courier partner: Amazon Shipping
                </div>
            </div>
        </div>
    );
};

/* ── Shared panel content ─────────────────────────────────────────────────── */
export const PanelContent = ({ result, pincode, setPincode, loading, error, setError, checkPincode, clear, onClose, source, useCurrentLocation, detectingLocation }) => {
    const isServiceable = result?.serviceable;
    return (
        <>
            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.9rem 1rem 0.75rem',
                background: 'var(--primary-color)',
                borderBottom: '1px solid var(--surface-border)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.18)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <i className="pi pi-truck" style={{ color: '#fff', fontSize: '0.85rem' }} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff', lineHeight: 1.2 }}>
                            Check Delivery
                        </div>
                        <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>
                            Enter pincode to check availability
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {result && (
                        <>
                            <DeliveryFeedbackTrigger pincode={result.pincode || pincode} source={source || 'delivery_panel'} />
                            <button
                                type="button"
                                onClick={clear}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 3,
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '999px', background: 'transparent',
                                    color: '#fff',
                                    fontSize: '0.65rem', fontWeight: 600,
                                    padding: '2px 8px', cursor: 'pointer',
                                }}
                            >
                                <i className="pi pi-refresh" style={{ fontSize: '0.6rem' }} />
                                Reset
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            width: 28, height: 28, borderRadius: '50%',
                            border: '1px solid rgba(255,255,255,0.3)',
                            background: 'rgba(255,255,255,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: '#fff', flexShrink: 0,
                        }}
                    >
                        <i className="pi pi-times" style={{ fontSize: '0.65rem' }} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <SavedAddressList onSelect={(pin) => { setPincode(pin); checkPincode(pin); onClose(); }} />

                <PinCodeInput
                    pincode={pincode}
                    onChange={(v) => { setPincode(v); setError(null); }}
                    onCheck={() => checkPincode()}
                    loading={loading}
                    error={error}
                    onUseLocation={useCurrentLocation}
                    detectingLocation={detectingLocation}
                />

                {result && (
                    <>
                        <div style={{
                            borderRadius: '0.75rem',
                            border: `1.5px solid ${isServiceable ? '#6ee7b7' : '#fdba74'}`,
                            background: isServiceable ? '#f0fdf4' : '#fff7ed',
                            padding: '0.85rem 1rem',
                            display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
                        }}>
                            <div style={{
                                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                background: isServiceable ? '#22c55e' : '#f97316',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <i className={`pi ${isServiceable ? 'pi-check' : 'pi-times'}`}
                                    style={{ color: '#fff', fontSize: '0.8rem' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {isServiceable ? (
                                    <>
                                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#15803d' }}>
                                            Delivery Available 🎉
                                        </div>
                                        <div style={{ fontSize: '0.78rem', color: '#166534', marginTop: '0.2rem' }}>
                                            {result.estimatedDays != null ? 'Delivered in' : 'Delivered within'}{' '}
                                            <strong>{formatDaysLabel(result.estimatedDays)}</strong>
                                            {' '}via <strong>{result.courier}</strong>
                                        </div>
                                        {result.deliveryDate && (
                                            <div style={{
                                                fontSize: '0.72rem', color: '#fff', marginTop: '0.35rem',
                                                background: '#16a34a', display: 'inline-block',
                                                padding: '2px 10px', borderRadius: '999px', fontWeight: 600,
                                            }}>
                                                Expected by {result.deliveryDate}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '0.68rem', color: '#089f3f', fontWeight: 600, marginTop: '0.3rem' }}>
                                            Pincode {result.pincode}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.4rem' }}>
                                            <i className="pi pi-info-circle" style={{ fontSize: '0.58rem', color: '#64748b', flexShrink: 0 }} />
                                            <span style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: 1.4 }}>
                                                {DELIVERY_TIMELINE_NOTE}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#c2410c' }}>
                                            Not Serviceable
                                        </div>
                                        <div style={{ fontSize: '0.78rem', color: '#9a3412', marginTop: '0.2rem' }}>
                                            Delivery not available to pincode <strong>{result.pincode}</strong>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <FastestDeliveryCard fastestDelivery={result.fastestDelivery} />

                        {/* Feedback badge — always shown once result is available */}
                        <DeliveryFeedbackTrigger
                            pincode={result.pincode || pincode}
                            source={source || 'delivery_panel'}
                            variant="badge"
                        />
                    </>
                )}
            </div>
        </>
    );
};


/* ── Bottom sheet (mobile + tablet) ──────────────────────────────────────── */
export const BottomSheet = ({ onClose, source, ...props }) =>
    createPortal(
        <>
            <style>{`
                @keyframes ennix-se-up {
                    from { transform: translateY(100%); }
                    to   { transform: translateY(0); }
                }
            `}</style>
            <div
                onClick={onClose}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200 }}
            />
            <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0,
                background: '#fff', borderRadius: '18px 18px 0 0',
                zIndex: 1201,
                animation: 'ennix-se-up 0.28s cubic-bezier(0.32,0.72,0,1)',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                maxHeight: '90vh', overflowY: 'auto',
            }}>
                {/* Handle */}
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2 }}>
                    <div style={{ width: 36, height: 4, borderRadius: 99, background: '#e2e8f0' }} />
                </div>
                <PanelContent onClose={onClose} source={source} {...props} />
            </div>
        </>,
        document.body
    );

/* ── Main component ───────────────────────────────────────────────────────── */
// `shipping` is required here — always the object returned by useShipping().
// Passed down by the two exported wrappers below so a sibling (e.g. a summary
// line elsewhere) can share the exact same live pincode/result instantly.
const ShippingEstimateBase = ({ source = 'product_card', buttonStyle, shipping }) => {
    const tooltipId = useId().replace(/:/g, '');
    const overlayRef = useRef(null);
    const [showSheet,  setShowSheet]  = useState(false);
    const [isCompact,  setIsCompact]  = useState(false);
    const [isWide,     setIsWide]     = useState(false);
    const { pincode, setPincode, result, loading, error, setError, checkPincode, clear, useCurrentLocation, detectingLocation, initializing } = shipping;
    const user = getAuthUser();

    // Once the account's saved delivery check has been fetched (or found to
    // be empty), fall back to auto-checking the default address's pincode.
    useEffect(() => {
        if (!user || initializing || result) return;
        fetch('/api/user/address')
            .then((r) => r.json())
            .then((json) => {
                if (!json.success) return;
                const list = json.data?.addresses || json.data || [];
                const def = list.find((a) => a.isDefault) || list[0];
                const pin = def?.pincode || def?.pinCode ? String(def.pincode || def.pinCode) : '';
                if (pin) checkPincode(pin);
            })
            .catch(() => {});
    }, [initializing]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const update = () => setIsCompact(window.innerWidth < 1024);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        const update = () => setIsWide(window.innerWidth > 940);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    const handleTrigger = (e) => {
        if (isCompact) {
            setShowSheet(true);
        } else {
            overlayRef.current?.toggle(e);
        }
    };

    const isServiceable = result?.serviceable;
    const tooltipText = result?.serviceable && result.courier
        ? `Serviced by ${result.courier}${result.deliveryDate ? ` · Expected by ${result.deliveryDate}` : ''}`
        : null;

    const btnColor = 'var(--primary-color)';

    const sharedProps = {
        result, pincode, setPincode, loading, error, setError, checkPincode, clear,
        useCurrentLocation, detectingLocation,
    };

    return (
        <>
            {tooltipText && (
                <Tooltip target={`.shipping-btn-${tooltipId}`} content={tooltipText} position="bottom" className="text-xs" />
            )}

            {/* Trigger button */}
            <button
                type="button"
                className={`shipping-btn-${tooltipId} surface-0`}
                onClick={handleTrigger}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
                    padding: '0.5rem 0.85rem 0.5rem 0.55rem',
                    borderRadius: '0.75rem',
                    border: `1.5px solid var(--primary-color)`,
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    flexShrink: 0,
                    boxShadow: '0 2px 12px rgba(11,49,99,0.18)',
                    textAlign: 'left',
                    ...buttonStyle,
                }}
            >
                <i
                    className={`pi ${result ? (isServiceable ? 'pi-map-marker' : 'pi-times-circle') : 'pi-map-marker'}`}
                    style={{ color: 'var(--primary-color)', fontSize: '1rem', flexShrink: 0 }}
                />

                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    {loading ? (
                        isWide ? (
                            <>
                                <Skeleton width="7.5rem" height="0.7rem" borderRadius="4px" />
                                <Skeleton width="4.5rem" height="0.62rem" borderRadius="4px" className="mt-1" />
                            </>
                        ) : (
                            <Skeleton width="6.5rem" height="0.7rem" borderRadius="4px" />
                        )
                    ) : !result ? (
                        <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Check Delivery
                        </span>
                    ) : isServiceable ? (
                        isWide ? (
                            <>
                                <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Delivered in {formatDaysLabel(result.estimatedDays)}{result.courier ? ` via ${result.courier}` : ''}
                                </span>
                                <span style={{ display: 'block', fontSize: '0.62rem', fontWeight: 600, color: 'var(--primary-color)', opacity: 0.65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Pincode {result.pincode}
                                </span>
                            </>
                        ) : (
                            <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {result.pincode} · {formatDaysLabel(result.estimatedDays)}{result.courier ? ` · ${result.courier}` : ''}
                            </span>
                        )
                    ) : (
                        <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#f97316', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {result.pincode} · Not Serviceable
                        </span>
                    )}
                </div>

                <i className="pi pi-chevron-down"
                    style={{ fontSize: '0.55rem', opacity: 0.4, marginLeft: 'auto', flexShrink: 0, color: btnColor }} />
            </button>

            <style>{`
                @keyframes ennix-pulse {
                    0%   { box-shadow: 0 0 0 0   rgba(27,184,167,0.5); }
                    70%  { box-shadow: 0 0 0 7px rgba(27,184,167,0);   }
                    100% { box-shadow: 0 0 0 0   rgba(27,184,167,0);   }
                }
            `}</style>

            {/* Desktop: popover anchored to the trigger button */}
            <OverlayPanel
                ref={overlayRef}
                style={{
                    width: 380,
                    borderRadius: '0.85rem',
                    overflow: 'hidden',
                    boxShadow: '0 12px 40px rgba(11,49,99,0.22)',
                }}
                pt={{ content: { style: { padding: 0 } } }}
            >
                <PanelContent onClose={() => overlayRef.current?.hide()} source={source} {...sharedProps} />
            </OverlayPanel>

            {/* Mobile + tablet: Bottom sheet */}
            {showSheet && (
                <BottomSheet onClose={() => setShowSheet(false)} source={source} {...sharedProps} />
            )}
        </>
    );
};

// Owns its own useShipping() instance — only mounted when no `shipping` prop is given,
// so callers that share state elsewhere don't pay for a second, redundant fetch.
const ShippingEstimateOwnState = (props) => {
    const shipping = useShipping();
    return <ShippingEstimateBase {...props} shipping={shipping} />;
};

/* ── Public component ─────────────────────────────────────────────────────── */
// Pass `shipping` (the object returned by useShipping()) when a sibling — e.g.
// a summary line — needs to reflect the same live pincode/result instantly.
// Omit it and this manages its own independent shipping state as before.
export const ShippingEstimate = ({ shipping, ...props }) =>
    shipping
        ? <ShippingEstimateBase {...props} shipping={shipping} />
        : <ShippingEstimateOwnState {...props} />;
